import {
  explicitStoryboardSheetUrl,
  normalizeStoryboardSheetUrl,
} from '@/lib/narrationStoryboardSheet';

function createNarrationSceneClientId() {
  return `scene-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

const PLACEHOLDER_BODY_RE = /^describe this panel in optional full script/i;

/** Strip all leading "Scene N:" prefixes (handles double-labeled lines from copy/paste). */
export function stripSceneNumberPrefix(text) {
  let s = String(text ?? '').trim();
  let prev;
  do {
    prev = s;
    const m = s.match(/^Scene\s+\d+\s*:\s*/i);
    if (m) s = s.slice(m[0].length).trim();
  } while (s !== prev);
  return s;
}

export function sceneBodyFromFields(scene) {
  const raw = scene?.text || scene?.narration_text || scene?.scene_text || '';
  return stripSceneNumberPrefix(raw);
}

/**
 * Sort panels top-to-bottom, left-to-right using normalized crop_box (per-mille).
 * Scenes without crop_box keep their relative order at the end.
 */
export function sortScenesByCropBoxReadingOrder(scenes) {
  if (!Array.isArray(scenes) || !scenes.length) return [];

  const items = scenes.map((scene, idx) => {
    const box = scene?.crop_box;
    if (!box || typeof box !== 'object') {
      return { scene, idx, row: Number.MAX_SAFE_INTEGER, cy: Number.MAX_SAFE_INTEGER, cx: idx };
    }
    const y = Number(box.y) || 0;
    const x = Number(box.x) || 0;
    const h = Number(box.height) || 0;
    const w = Number(box.width) || 0;
    const cy = y + h / 2;
    const cx = x + w / 2;
    const row = Math.floor(cy / 100);
    return { scene, idx, row, cy, cx };
  });

  items.sort((a, b) => {
    if (a.row !== b.row) return a.row - b.row;
    if (a.cx !== b.cx) return a.cx - b.cx;
    if (a.cy !== b.cy) return a.cy - b.cy;
    return a.idx - b.idx;
  });

  return items.map((it) => it.scene);
}

/** Group key for scenes extracted from the same storyboard sheet. */
export function storyboardSheetGroupKey(scene) {
  if (scene?.from_multi_upload) return '__multi__';
  const url = explicitStoryboardSheetUrl(scene);
  if (url) return normalizeStoryboardSheetUrl(url) || url;
  return '__ungrouped__';
}

/**
 * Sort each storyboard batch by panel position; keep batch order (storyboard 1, then 2, …).
 * Never compare crop_box across different sheets — coordinates are sheet-relative.
 */
export function sortScenesByStoryboardGroupsReadingOrder(scenes) {
  if (!Array.isArray(scenes) || !scenes.length) return [];

  const groupOrder = [];
  const groups = new Map();

  scenes.forEach((scene) => {
    const key = storyboardSheetGroupKey(scene);
    if (!groups.has(key)) {
      groups.set(key, []);
      groupOrder.push(key);
    }
    groups.get(key).push(scene);
  });

  const result = [];
  for (const key of groupOrder) {
    const bucket = groups.get(key) || [];
    if (key === '__multi__' || key === '__ungrouped__') {
      result.push(...bucket);
      continue;
    }
    const hasCrop = bucket.some((s) => s?.crop_box);
    result.push(...(hasCrop ? sortScenesByCropBoxReadingOrder(bucket) : bucket));
  }
  return result;
}

/** Append a manually extracted scene at the end of its storyboard group (extraction order). */
export function insertSceneIntoStoryboardGroup(scenes, newScene, sheetUrl) {
  const list = Array.isArray(scenes) ? [...scenes] : [];
  if (!list.length) return [newScene];

  const sheetKey =
    normalizeStoryboardSheetUrl(sheetUrl)
    || storyboardSheetGroupKey(newScene);

  let insertAt = list.length;
  let lastInGroup = -1;
  list.forEach((scene, idx) => {
    if (storyboardSheetGroupKey(scene) === sheetKey) lastInGroup = idx;
  });
  if (lastInGroup >= 0) insertAt = lastInGroup + 1;

  return [...list.slice(0, insertAt), newScene, ...list.slice(insertAt)];
}

/** Renumber segment_number and rewrite Scene N: labels to match array order. */
export function relabelImageStoryScenes(scenes) {
  return scenes.map((scene, idx) => {
    const n = idx + 1;
    let body = sceneBodyFromFields(scene);
    if (!body || PLACEHOLDER_BODY_RE.test(body)) {
      body = 'describe this panel in Optional Full Script before generating narration.';
    }
    return {
      ...scene,
      client_id: scene.client_id || createNarrationSceneClientId(),
      sort_order: idx,
      segment_number: n,
      panel_number: n,
      text: body,
      scene_text: body,
      narration_text: body,
      visual: scene?.visual ? (stripSceneNumberPrefix(scene.visual) || body) : body,
    };
  });
}

/** Keep first occurrence when the same client_id appears twice (stale concurrent updates). */
export function dedupeScenesByClientId(scenes) {
  if (!Array.isArray(scenes)) return [];
  const seen = new Set();
  return scenes.filter((scene) => {
    const id = scene?.client_id;
    if (!id) return true;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

/**
 * Sort by on-sheet position (optional), then renumber and rename Scene 1…N labels.
 */
export function finalizeImageStoryScenes(scenes, { orderByCropBox = false } = {}) {
  if (!Array.isArray(scenes) || !scenes.length) return [];
  const hasCropLayout = scenes.some((s) => s?.crop_box);
  let next =
    orderByCropBox && hasCropLayout
      ? sortScenesByStoryboardGroupsReadingOrder(scenes)
      : [...scenes];
  next = dedupeScenesByClientId(
    next.map((s) => ({
      ...s,
      client_id: s.client_id || createNarrationSceneClientId(),
    })),
  );
  return relabelImageStoryScenes(next);
}
